import { useState } from "react"
import { Alert, DatePicker, Modal } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import type { TenantSummary } from "@csbi/contracts"

/**
 * ⚠ **A dialog rather than a date field in the Edit form**, and that is the whole point of the file. Offboarding
 * changes how every chart in the product treats this customer — it drops them out of cross-customer statistics
 * and stops collection. A date input sitting among name / address / schedule gives no chance to say any of that,
 * is easy to fill in by accident, and is hard to find on purpose.
 */

export type OffboardDialogProps = {
  tenant: TenantSummary | null
  onCancel: () => void
  onConfirm: (offboardedAt: string) => Promise<unknown>
}

/**
 * ⚠ **The caller keys this on the customer id**, which is what resets the date between customers.
 */
export function OffboardDialog({ tenant, onCancel, onConfirm }: OffboardDialogProps) {
  const [date, setDate] = useState<Dayjs>(() => (tenant?.offboardedAt ? dayjs(tenant.offboardedAt) : dayjs()))
  const [saving, setSaving] = useState(false)

  const onboarded = tenant?.onboardedAt ? dayjs(tenant.onboardedAt) : null
  const tooEarly = Boolean(onboarded && date.isBefore(onboarded, "day"))

  return (
    <Modal
      open={Boolean(tenant)}
      title={tenant ? `Offboard ${tenant.displayName}?` : ""}
      okText="Offboard"
      okButtonProps={{ disabled: tooEarly, loading: saving }}
      onCancel={onCancel}
      onOk={async () => {
        setSaving(true)
        try {
          await onConfirm(date.format("YYYY-MM-DD"))
        } finally {
          setSaving(false)
        }
      }}
      destroyOnHidden
    >
      <p style={{ marginTop: 0 }}>Collection stops, and this deployment leaves the default cross-customer statistics.</p>

      <ul style={{ paddingInlineStart: 20, margin: "0 0 16px", color: "var(--csbi-ink-secondary)", lineHeight: 1.7 }}>
        <li>All collected history is kept and stays queryable.</li>
        <li>They move to the Churned group in the customer picker; picking them there still works.</li>
        <li>Reversible — clearing the date brings them back, keeping their colour.</li>
      </ul>

      <label htmlFor="offboarded-at" style={{ display: "block", marginBottom: 6, fontSize: 13 }}>
        Left on
      </label>
      <DatePicker
        id="offboarded-at"
        value={date}
        onChange={(d) => d && setDate(d)}
        allowClear={false}
        style={{ width: "100%" }}
        // Future-dating a departure would hide a customer who is still using the product today
        maxDate={dayjs()}
        {...(onboarded ? { minDate: onboarded } : {})}
      />
      {tooEarly && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 12 }}
          message={`They joined on ${onboarded?.format("YYYY-MM-DD")}, so they cannot have left before that.`}
        />
      )}
    </Modal>
  )
}
